import React, { useEffect, useState } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom'; // Import useParams, Link, and useNavigate
import { BACKEND_URL } from '../Constants'; // Assuming BACKEND_URL is defined

const BACKEND_QUIZZES = `${BACKEND_URL}/api/chatbotai-quizzes`;
const BACKEND_PROGRESS = `${BACKEND_URL}/api/quiz-progress`; // Endpoint for quiz progress
const BACKEND_USERS = `${BACKEND_URL}/api/users`; // Endpoint for fetching user details

const QuizWindow = () => {
  const { id } = useParams(); // Get quiz ID from URL
  const navigate = useNavigate(); // For programmatic navigation

  const [quiz, setQuiz] = useState(null); // Stores the entire quiz object
  const [questions, setQuestions] = useState([]); // Stores just the questions array
  const [currentQuestionIndex, setCurrentQuestionIndex] = useState(0);
  const [userAnswers, setUserAnswers] = useState({}); // Stores user's selected answers

  const [finished, setFinished] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  // States for user login and email handling
  const [loggedInUserId, setLoggedInUserId] = useState(null);
  const [loggedInUserEmail, setLoggedInUserEmail] = useState('');
  const [isFetchingUserEmail, setIsFetchingUserEmail] = useState(false);

  // States for quiz progression logic
  const [allQuizzes, setAllQuizzes] = useState([]); // All quizzes available
  const [completedQuizIds, setCompletedQuizIds] = useState(new Set()); // IDs of quizzes user has completed
  const [nextUntakenQuizId, setNextUntakenQuizId] = useState(null); // ID of the next quiz to suggest

  const [submissionStatus, setSubmissionStatus] = useState(''); // To show feedback on submission

  // Helper function to find the next untaken quiz
  const findNextUntakenQuiz = (currentQuizId, allQuizzesList, completedIdsSet) => {
    if (!allQuizzesList || allQuizzesList.length === 0) return null;

    // Filter out quizzes that are already completed by the user
    const untakenQuizzes = allQuizzesList.filter(q => !completedIdsSet.has(q._id));

    // If there are untaken quizzes, find the first one that is not the current quiz (if currentQuizId exists)
    if (untakenQuizzes.length > 0) {
      if (currentQuizId) {
        // Try to find an untaken quiz that is different from the one just finished/viewed
        const nextQuiz = untakenQuizzes.find(q => q._id !== currentQuizId);
        if (nextQuiz) {
          return nextQuiz._id;
        } else if (untakenQuizzes.length > 0 && !completedIdsSet.has(currentQuizId)) {
          // If the current quiz is untaken and there are no other untaken quizzes,
          // it means the current one is the only untaken one left.
          // This case should ideally not be hit if the currentQuizId is already completed.
          return untakenQuizzes[0]._id; // Fallback to first untaken if current one is the only one
        } else if (untakenQuizzes.length > 0) {
          // If the current quiz was just completed, and there are other untaken quizzes,
          // just return the first one from the untaken list.
          return untakenQuizzes[0]._id;
        }
      } else {
        // If no currentQuizId (e.g., initial load), just return the first untaken quiz
        return untakenQuizzes[0]._id;
      }
    }
    return null; // All quizzes taken or no quizzes available
  };


  // Effect to check login status and fetch user email if logged in
  useEffect(() => {
    const userId = localStorage.getItem('userId');
    const token = localStorage.getItem('token');

    if (userId && token) {
      setLoggedInUserId(userId);
      setIsFetchingUserEmail(true);
      const fetchUserEmail = async () => {
        try {
          const response = await fetch(`${BACKEND_USERS}/${userId}`, {
            headers: {
              // 'Authorization': `Bearer ${token}` // Uncomment if your user API is protected
            }
          });
          if (!response.ok) {
            const errorText = await response.text();
            throw new Error(`Failed to fetch user email: Status ${response.status}, Body: ${errorText}`);
          }
          const userData = await response.json();
          setLoggedInUserEmail(userData.email);
        } catch (err) {
          console.error("Error fetching user email:", err);
          setLoggedInUserEmail('');
          setLoggedInUserId(null);
        } finally {
          setIsFetchingUserEmail(false);
        }
      };
      fetchUserEmail();
    } else {
      setLoggedInUserId(null);
      setLoggedInUserEmail('');
      setIsFetchingUserEmail(false);
    }
  }, []);

  // Effect to fetch quiz data and user's completed quizzes
  useEffect(() => {
    const fetchAllQuizAndUserData = async () => {
      setLoading(true);
      setError(null);

      let targetQuizId = id;
      let fetchedAllQuizzes = [];
      let fetchedCompletedQuizIds = new Set();

      try {
        // 1. Fetch all quizzes
        const allQuizzesResponse = await fetch(BACKEND_QUIZZES);
        if (!allQuizzesResponse.ok) {
          const errorText = await allQuizzesResponse.text();
          throw new Error(`HTTP error! Failed to fetch all quizzes: ${allQuizzesResponse.status}, Body: ${errorText}`);
        }
        fetchedAllQuizzes = await allQuizzesResponse.json();
        setAllQuizzes(fetchedAllQuizzes);

        // 2. Fetch completed quizzes for the logged-in user (if applicable)
        if (loggedInUserId) {
          const completedResponse = await fetch(`${BACKEND_PROGRESS}/${loggedInUserId}`);
          if (!completedResponse.ok) {
            const errorText = await completedResponse.text();
            console.warn(`Failed to fetch completed quizzes for user ${loggedInUserId}: ${completedResponse.status}, Body: ${errorText}`);
            // Don't throw error, just proceed without completed quiz info
          } else {
            const completedData = await completedResponse.json();
            completedData.forEach(record => fetchedCompletedQuizIds.add(record.quizId._id || record.quizId)); // Ensure we get the ID
            setCompletedQuizIds(fetchedCompletedQuizIds);
          }
        }

        // 3. Determine the quiz to load
        if (!id) { // If no specific ID in URL, find the first untaken
          targetQuizId = findNextUntakenQuiz(null, fetchedAllQuizzes, fetchedCompletedQuizIds);
          if (!targetQuizId) {
            setError('All quizzes completed or no quizzes available.');
            setLoading(false);
            return;
          }
        }

        // 4. Fetch the specific target quiz
        const response = await fetch(`${BACKEND_QUIZZES}/${targetQuizId}`);
        if (!response.ok) {
          const errorText = await response.text();
          throw new Error(`HTTP error! Failed to fetch quiz ${targetQuizId}: ${response.status}, Body: ${errorText}`);
        }
        const data = await response.json();
        setQuiz(data);
        setQuestions(data.questions || []);

        // Reset quiz state variables for a new quiz load
        setCurrentQuestionIndex(0);
        setUserAnswers({});
        setFinished(false);
        setSubmissionStatus('');

      } catch (err) {
        console.error(`Error in fetchAllQuizAndUserData:`, err);
        setError(err.message || 'An unexpected error occurred while loading the quiz.');
      } finally {
        setLoading(false);
      }
    };

    // Only run if loggedInUserId is determined (or if no ID is provided, it will try to find a default)
    // This ensures completed quizzes are fetched after user ID is known
    if (loggedInUserId !== null || !id) { // loggedInUserId is null initially, then string, or null if not logged in
      fetchAllQuizAndUserData();
    }
  }, [id, loggedInUserId]); // Re-run if ID or loggedInUserId changes

  const handleAnswerSelection = (questionId, selectedChoiceKey) => {
    setUserAnswers(prevAnswers => ({
      ...prevAnswers,
      [questionId]: selectedChoiceKey
    }));
  };

  const handleNext = async () => {
    if (currentQuestionIndex < questions.length - 1) {
      setCurrentQuestionIndex(prevIndex => prevIndex + 1);
    } else {
      setFinished(true);
      // Automatically attempt to submit the quiz if logged in
      if (loggedInUserId) {
        setSubmissionStatus('Submitting your results...');
        await handleSubmitQuiz(); // Call submit directly
      } else {
        setSubmissionStatus('Please log in to save your quiz results.');
      }
    }
  };

  const handlePrevious = () => {
    if (currentQuestionIndex > 0) {
      setCurrentQuestionIndex(prevIndex => prevIndex - 1);
    }
  };

  const handleSubmitQuiz = async () => {
    if (!loggedInUserId) {
      setSubmissionStatus('Cannot save: User not logged in.');
      console.error('Submission failed: No loggedInUserId available.');
      return;
    }

    setSubmissionStatus('Submitting answers...');
    try {
      const payload = {
        quizId: quiz._id,
        userId: loggedInUserId, // Send userId to backend
        answers: userAnswers
      };

      const response = await fetch(BACKEND_PROGRESS, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });

      if (!response.ok) {
        const errorData = await response.json();
        throw new Error(errorData.error || 'Failed to submit quiz results.');
      }

      setSubmissionStatus('✅ Quiz submitted successfully!');
      console.log('Quiz results submitted:', userAnswers);

      // After successful submission, update completedQuizIds and find next untaken quiz
      setCompletedQuizIds(prev => new Set(prev).add(quiz._id)); // Add current quiz to completed
      const nextId = findNextUntakenQuiz(quiz._id, allQuizzes, new Set(completedQuizIds).add(quiz._id));
      setNextUntakenQuizId(nextId);

    } catch (err) {
      console.error('Error submitting quiz results:', err);
      setSubmissionStatus(`❌ Submission failed: ${err.message}`);
    }
  };

  // --- Render Logic ---
  if (loading) {
    return (
      <div className="text-center py-4">
        <p>Loading quiz...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="text-center py-4">
        <p className="text-danger">Error: {error}</p>
        <div className="mt-3">
          <Link to="/dashboard/chatbotai-quizzes" className="btn btn-primary">Back to Quizzes</Link>
        </div>
      </div>
    );
  }

  if (!quiz || questions.length === 0) {
    return (
      <div className="text-center py-4">
        <p className="text-muted">No questions found for this quiz.</p>
        <div className="mt-3">
          <Link to="/dashboard/chatbotai-quizzes" className="btn btn-primary">Back to Quizzes</Link>
        </div>
      </div>
    );
  }

  if (finished) {
    return (
      <div className="py-4">
        <div className="card">
          <div className="card-body text-center">
            <h3 className="card-title">Thank you for completing the quiz!</h3>
            <p className="text-muted">Your answers have been recorded locally.</p>

            {isFetchingUserEmail ? (
              <p className="text-info mt-3">Loading user data for submission status...</p>
            ) : loggedInUserId && loggedInUserEmail ? (
              <p className="mt-3">Results for: <strong>{loggedInUserEmail}</strong></p>
            ) : (
              <p className="text-warning mt-3">You are not logged in. Quiz progress will not be saved. Please log in to save your results.</p>
            )}

            {submissionStatus && (
              <p className={`mt-3 ${submissionStatus.startsWith('✅') ? 'text-success' : submissionStatus.startsWith('❌') ? 'text-danger' : 'text-info'}`}>
                {submissionStatus}
              </p>
            )}

            {/* Conditional "Next Quiz" or "Congrats" button */}
            {loggedInUserId && nextUntakenQuizId ? (
              <div className="mt-4">
                <Link to={`/dashboard/chatbotai-quizzes/${nextUntakenQuizId}/start`} className="btn btn-success">
                  Next Quiz
                </Link>
              </div>
            ) : loggedInUserId ? ( // All quizzes taken for logged-in user
              <div className="mt-4">
                <p className="text-success font-weight-bold">🎉 Congratulations! You have completed all available quizzes! 🎉</p>
              </div>
            ) : null} {/* If not logged in, no "Next Quiz" or "Congrats" */}
          </div>
        </div>
      </div>
    );
  }

  const currentQuestion = questions[currentQuestionIndex];
  const questionOptions = Object.keys(currentQuestion.choices).map(key => ({
    key: key,
    text: currentQuestion.choices[key]
  }));

  const isLastQuestion = currentQuestionIndex === questions.length - 1;
  const hasPreviousQuestion = currentQuestionIndex > 0;
  const selectedAnswerKey = userAnswers[currentQuestion._id];

  return (
    <div className="py-4">
      <h2 className="text-center mb-4">{quiz.title}</h2>
      <p className="text-center text-muted">{quiz.description}</p>

      <div className="card">
        <div className="card-body">
          <h5 className="card-title">Question {currentQuestionIndex + 1} of {questions.length}</h5>
          <p className="card-text mb-4">{currentQuestion.text}</p>

          <ul className="list-group list-group-flush">
            {questionOptions.map((opt) => (
              <li key={opt.key} className="list-group-item">
                <button
                  className={`btn btn-block ${selectedAnswerKey === opt.key ? 'btn-primary' : 'btn-outline-secondary'}`}
                  onClick={() => handleAnswerSelection(currentQuestion._id, opt.key)}
                  style={{ textAlign: 'left', width: '100%' }}
                >
                  {opt.text}
                </button>
              </li>
            ))}
          </ul>

          <div className="d-flex justify-content-between mt-4">
            {hasPreviousQuestion ? (
              <button
                className="btn btn-secondary"
                onClick={handlePrevious}
              >
                Previous
              </button>
            ) : (
              <div style={{ width: '80px' }}></div>
            )}

            <button
              className={`btn ${isLastQuestion ? 'btn-success' : 'btn-primary'}`}
              onClick={handleNext}
              disabled={!selectedAnswerKey}
            >
              {isLastQuestion ? 'Finish Quiz' : 'Next Question'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default QuizWindow;